import { Config } from '../video-data/config';
import { assertNumber } from '../validators';
import { BaseTimeline } from './BaseTimeline';

/**
 *
 * A type of timeline that is automatically placed in sequence under a parent.
 *
 * The relativeStartTime is automatically calculated and cannot be set directly. It's possible
 * to shift a SequentialTimeline from its default position by using the offset property.
 * Note that the offset cannot position a timeline to start before its parent.
 *
 * Changing the position or duration of a SequentialTimeline will result in any following
 * siblings automatically getting updated.
 */
export class SequentialTimeline extends BaseTimeline {
    override _classType = Config.SEQUENTIAL_TIMELINE_TYPE;
    #offset = 0;

    override _moveFromTo(parent: BaseTimeline, oldIndex: number, newIndex: number) {
        super._moveFromTo(parent, oldIndex, newIndex);

        // start updating at the first changed timeline to recalculate relative start times
        const updateIndex = Math.min(oldIndex, newIndex);
        parent.children[updateIndex].requiresUpdate = true;
    }

    override _removeFromParent(parent: BaseTimeline, index: number) {
        super._removeFromParent(parent, index);

        // start updating at the first changed timeline to recalculate relative start times
        if (parent.children.length > index) {
            parent.children[index].requiresUpdate = true;
        }
    }

    override _update() {
        super._update();
        this._updateRelativeStartTime();
    }

    _updateRelativeStartTime() {
        const parent = this.parent;
        if (!parent) {
            this._relativeStartTime = undefined;
            return;
        }

        const index = parent.getChildIndex(this);
        if (index === 0) {
            this._relativeStartTime = this.offset;
        } else {
            this._relativeStartTime = this.offset + parent.children[index - 1].relativeEndTime;
        }

        // a timeline cannot start before it's parent
        if (this._relativeStartTime < 0) {
            this._relativeStartTime = 0;
        }

        const next = parent.children[index + 1];
        if (next) {
            if (!(next instanceof SequentialTimeline)) {
                throw new Error('Sibling timelines must be the same type');
            }
            // Any sequential timelines after this one need to be recalculated as well
            next._updateRelativeStartTime();
        }
    }

    /**
     * A positive/negative amount to offset this timeline by.
     *
     * Overlapping timelines can be created by setting a negative offset.
     * ----------------------------------------------------------------------
     * |[_ parent timeline ________________________________________________]|
     * |[_ sequential timeline #1 __________________]                       |
     * |                           <~~~ - offset ~~~>                       |
     * |                           [_ sequential timeline #2 ______________]|
     * ----------------------------------------------------------------------
     *
     * Timelines with empty gaps can be created by setting a positive offset.
     * ------------------------------------------------------==----------------
     * |[_ parent timeline __________________________________________________]|
     * |[_ sequential timeline #1 _]                                          |
     * |                            <~ + offset ~>                            |
     * |                                          [_ sequential timeline #2 _]|
     * ------------------------------------------------------------------------
     */
    get offset(): number {
        return this.#offset;
    }

    set offset(value: number) {
        if (value === this.#offset) {
            return;
        }
        assertNumber(value);
        this.#offset = value;
        this.requiresUpdate = true;
    }
}
